import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useMemo, useRef, useState } from 'react';
const SCRIPT_ELEMENT_ID = 'authorizenet-acceptui-script';
export const AcceptUIButton = (props) => {
    const buttonId = useId().replace(/:/g, '_');
    const handlerName = useMemo(() => `acceptUiResponseHandler_${buttonId}`, [buttonId]);
    const [scriptReady, setScriptReady] = useState(false);
    const injectedRef = useRef(false);
    const handedOffRef = useRef(null);
    // AcceptUI.js binds to the button when it executes, so a fresh copy is injected
    // per mounted instance. Guarded by a ref because StrictMode double-invokes mount
    // effects, and removing a script element does not undo the code it already ran —
    // injecting twice would bind the button twice and submit the card twice.
    useEffect(() => {
        if (!injectedRef.current) {
            injectedRef.current = true;
            setScriptReady(false);
            const prior = document.getElementById(SCRIPT_ELEMENT_ID);
            if (prior) {
                prior.remove();
            }
            const script = document.createElement('script');
            script.id = SCRIPT_ELEMENT_ID;
            script.src = `${props.config.scriptUrl}?ts=${Date.now()}`;
            script.async = true;
            script.charset = 'utf-8';
            script.onload = () => setScriptReady(true);
            script.onerror = props.onLoadError;
            document.body.appendChild(script);
        }
    }, []);
    useEffect(() => {
        const handler = (response) => {
            var _a, _b;
            if (response.messages.resultCode === 'Error') {
                props.onFailure(response.messages.message.map(x => x.text).join('\n'));
            }
            else if (!((_a = response.opaqueData) === null || _a === void 0 ? void 0 : _a.dataDescriptor) || !((_b = response.opaqueData) === null || _b === void 0 ? void 0 : _b.dataValue)) {
                props.onFailure('Authorize.Net did not return payment token data.');
            }
            // The opaque token is single use. Handing the same one off twice races the
            // first request and loses, so keyed on the token rather than a plain flag —
            // a genuinely new card still goes through.
            else if (handedOffRef.current !== response.opaqueData.dataValue) {
                handedOffRef.current = response.opaqueData.dataValue;
                props.onToken({
                    'token': response.opaqueData.dataValue,
                    'descriptor': response.opaqueData.dataDescriptor,
                });
            }
        };
        Reflect.set(window, handlerName, handler);
        return () => {
            Reflect.deleteProperty(window, handlerName);
        };
    }, [handlerName]);
    return _jsx(_Fragment, { children: _jsx("button", { id: buttonId, type: "button", className: `AcceptUI ${props.className}`, disabled: !!props.disabled || !scriptReady, "data-billingaddressoptions": JSON.stringify({ 'show': true, 'required': false }), "data-apiloginid": props.config.merchantId, "data-clientkey": props.config.publicKey, "data-acceptuiformbtntxt": "Add Card", "data-acceptuiformheadertxt": "Payment Information", "data-paymentoptions": JSON.stringify({ 'showCreditCard': true, 'showBankAccount': false }), "data-responsehandler": handlerName, children: props.children }) });
};
